import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getToken } from '../lib/api';
import { useToast } from './Toast';

const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { Authorization: `Bearer ${getToken()}`, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

// DID inventory picker — autosuggests DIDs from the trunk pool and shows what
// each is currently routed to (mirrors 3CX's DID assignment behaviour). Picking
// a DID that's already assigned elsewhere asks yes/no, then TAKES IT OVER on
// save — the server sync removes the old owner's inbound rule (#82). Shared by
// the Ring Group and Digital Receptionist editors (extracted from RingGroupEditor).
// `selfRef` is the owning entity's extension so its own DIDs read as "yours".
export default function DidPicker({ value, onChange, selfRef }: { value: any; onChange: (v: any[]) => void; selfRef?: string }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const inv = useQuery<any[]>({ queryKey: ['did-inventory'], queryFn: () => fJson('/api/dids') });
  const norm = (d: string) => String(d || '').replace(/[^0-9]/g, '');
  const selected: any[] = Array.isArray(value)
    ? value.map((v: any) => (typeof v === 'string' ? { number: v } : v))
    : String(value || '').split(',').map(s => s.trim()).filter(Boolean).map(n => ({ number: n }));
  const all = inv.data || [];
  const selSet = new Set(selected.map(s => norm(s.number)));
  const matches = all.filter(d => !selSet.has(norm(d.number)) && (!text || norm(d.number).includes(norm(text)))).slice(0, 8);
  const isElsewhere = (d: any) => d.assigned && !(selfRef && String(d.assigned.destination) === String(selfRef));

  function add(d: any) {
    if (isElsewhere(d)) {
      const ok = window.confirm(
        `${d.number} is already assigned to ${d.assigned.label}.\n\n` +
        `Take it over? After you save, calls to ${d.number} will come HERE instead, ` +
        `and it will no longer be assigned to ${d.assigned.label}.`);
      if (!ok) return;
      toast('info', `${d.number} will be taken over from ${d.assigned.label} when you save.`);
    }
    onChange([...selected, { number: d.number, trunk_uuid: d.trunk_uuid }]);
    setText(''); setOpen(false);
  }
  function remove(num: string) { onChange(selected.filter(s => norm(s.number) !== norm(num))); }

  return (
    <div className="relative">
      <div className="input flex flex-wrap gap-1 min-h-[38px] items-center" onClick={() => setOpen(true)}>
        {selected.map(s => (
          <span key={s.number} className="inline-flex items-center gap-1 bg-elevated rounded px-1.5 py-0.5 text-xs font-mono">
            {s.number}
            <button type="button" onClick={e => { e.stopPropagation(); remove(s.number); }} className="text-textSecondary hover:text-danger">×</button>
          </span>
        ))}
        <input className="flex-1 bg-transparent outline-none text-sm min-w-[110px]"
          value={text} placeholder={selected.length ? '' : 'Type to search DIDs…'}
          onChange={e => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)} />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 left-0 right-0 card max-h-60 overflow-auto py-1">
          {inv.isLoading && <div className="px-3 py-2 text-xs text-textSecondary">Loading DIDs…</div>}
          {!inv.isLoading && !all.length && (
            <div className="px-3 py-2 text-xs text-textSecondary">No DIDs available yet — add DID numbers on a SIP trunk first (Voice &amp; Chat → open a trunk → DID Numbers tab).</div>
          )}
          {!inv.isLoading && all.length > 0 && !matches.length && (
            <div className="px-3 py-2 text-xs text-textSecondary">No matching DIDs.</div>
          )}
          {matches.map(d => {
            const elsewhere = isElsewhere(d);
            return (
              <button type="button" key={d.number}
                onMouseDown={e => { e.preventDefault(); add(d); }}
                className="w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-2 hover:bg-elevated">
                <span className="font-mono">{d.number}</span>
                {d.assigned
                  ? <span className={`text-xs ${elsewhere ? 'text-warning' : 'text-success'}`}>→ {d.assigned.label}</span>
                  : <span className="text-xs text-success">available{d.trunk ? ` · ${d.trunk}` : ''}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
